import { useSensorLogs, LogLevel } from "@/api/v0/sensor-logs"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Trash2, Wifi, WifiOff } from "lucide-react"
import { cn } from "@/lib/utils"

interface SensorLogsViewerProps {
  sensorId: string
  className?: string
}

const getLogLevelColor = (level: LogLevel) => {
  switch (level) {
    case 'debug':
      return 'bg-gray-500 text-gray-100'
    case 'info':
      return 'bg-blue-500 text-blue-100'
    case 'warning':
      return 'bg-yellow-500 text-yellow-100'
    case 'error':
      return 'bg-red-500 text-red-100'
    case 'fatal':
      return 'bg-red-700 text-red-100'
    default:
      return 'bg-gray-500 text-gray-100'
  }
}

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return 'N/A'
  try {
    return new Date(timestamp).toLocaleTimeString()
  } catch {
    return 'Invalid'
  }
}

const getLogLevel = (level: any): LogLevel => {
  if (!level) return 'info'
  const levelStr = String(level).toLowerCase()
  if (['debug', 'info', 'warning', 'error', 'fatal'].includes(levelStr)) {
    return levelStr as LogLevel
  }
  return 'info'
}

export const SensorLogsViewer = ({ sensorId, className }: SensorLogsViewerProps) => {
  const { logs, clearLogs, isConnected, isLoading } = useSensorLogs(sensorId)



  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          Live Sensor Logs
          {isConnected ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={clearLogs}
          className="h-8 w-8 p-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-64 w-full rounded border border-neutral-800 bg-neutral-950 p-3">
          {logs.length === 0 ? (
            <div className="text-center text-neutral-500 py-8">
              {isLoading ? "Loading..." : isConnected ? "No logs yet..." : "Connecting to sensor logs..."}
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => {
                const level = getLogLevel(log.level)
                return (
                  <div
                    key={log.id || `log-${Date.now()}-${Math.random()}`}
                    className="flex items-start gap-2 text-sm font-mono"
                  >
                    <span className="text-neutral-400 min-w-[80px]">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    <Badge
                      variant="secondary"
                      className={cn("text-xs", getLogLevelColor(level))}
                    >
                      {level.toUpperCase()}
                    </Badge>
                    <span className="text-neutral-300 flex-1 break-words">
                      {log.message || 'No message'}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>
        <div className="mt-2 text-xs text-neutral-500">
          {logs.length} log{logs.length !== 1 ? 's' : ''} received
          {isLoading && " (loading...)"}
        </div>
      </CardContent>
    </Card>
  )
} 